/**
 * Person-first labels for Catchball / Catchball possession + request framing.
 *
 * Turns-first possession keys the holder by org unit and nulls
 * `currentHolderUserId` — callers must still resolve a human name (claimed
 * user → level-config assignee at the stop → org/stop label). Never return
 * the vague “Someone”, and never use the viewer’s tier as “Request from …”.
 *
 * Prefer `displayNamesByUserId` (from resolve_user_display_names RPC) over
 * workspace participant lists — passers often cannot see leaf assignees in
 * get_workspace_participants, which previously made “Cloud Servers holds the
 * ball” appear instead of “Blake”.
 */

export type CatchballLabelUser = {
  id: string;
  name?: string | null;
  email?: string | null;
};

export type CatchballLevelConfigSlice = {
  executive?: Array<{ userId?: string; orgUnitId?: string; groupId?: string }>;
  middle?: Array<{ userId?: string; orgUnitId?: string; groupId?: string }>;
  bottom?: Array<{ userId?: string; orgUnitId?: string; groupId?: string }>;
};

function labelForUser(
  users: readonly CatchballLabelUser[],
  id: string | null | undefined,
  displayNamesByUserId?: Readonly<Record<string, string>> | null,
): string | null {
  if (!id) return null;
  const fromMap = displayNamesByUserId?.[id]?.trim();
  if (fromMap && fromMap !== id) return fromMap;
  const u = users.find((x) => x.id === id);
  const label = u?.name?.trim() || u?.email?.trim();
  if (label) return label;
  // Map may only have the raw id when RPC hasn't returned yet — don't treat as a name.
  return null;
}

/** Named user ids on level_config at a Path stop (excludes empty / group-only rows). */
export function levelConfigAssigneeUserIds(
  levelConfig: CatchballLevelConfigSlice | null | undefined,
  holderOrgUnitId: string | null | undefined,
): string[] {
  if (!holderOrgUnitId || !levelConfig) return [];
  const ids: string[] = [];
  for (const tier of ['executive', 'middle', 'bottom'] as const) {
    for (const p of levelConfig[tier] ?? []) {
      if (p.orgUnitId === holderOrgUnitId && p.userId?.trim()) {
        ids.push(p.userId.trim());
      }
    }
  }
  return Array.from(new Set(ids));
}

export function resolveBallHolderLabel(input: {
  users: readonly CatchballLabelUser[];
  /** Turns override may null this even when the loop still has a claim row. */
  currentHolderUserId?: string | null;
  loopBallHolderUserId?: string | null;
  holderOrgUnitId?: string | null;
  levelConfig?: CatchballLevelConfigSlice | null;
  /** Org-unit / virtual recipient label from org-aware access. */
  currentHolderLabel?: string | null;
  /**
   * Cross-user display names (resolve_user_display_names). Preferred over
   * `users` so leaf assignees resolve for passers who aren't in the same
   * participant fetch set.
   */
  displayNamesByUserId?: Readonly<Record<string, string>> | null;
  /** Final fallback when nothing resolves — never “Someone”. */
  fallback?: string;
}): string {
  const display = input.displayNamesByUserId ?? null;

  const claimed = labelForUser(
    input.users,
    input.currentHolderUserId ?? input.loopBallHolderUserId ?? null,
    display,
  );
  if (claimed) return claimed;

  const holderOrgId = input.holderOrgUnitId ?? null;
  if (holderOrgId && input.levelConfig) {
    for (const userId of levelConfigAssigneeUserIds(input.levelConfig, holderOrgId)) {
      const fromConfig = labelForUser(input.users, userId, display);
      if (fromConfig) return fromConfig;
    }
  }

  if (input.currentHolderLabel?.trim()) return input.currentHolderLabel.trim();
  return input.fallback ?? 'the current holder';
}

export function resolveRequestFromLabel(input: {
  users: readonly CatchballLabelUser[];
  requestIssuedBy?: string | null;
  loopCreatedBy?: string | null;
  cycleCreatedBy?: string | null;
  displayNamesByUserId?: Readonly<Record<string, string>> | null;
}): string | undefined {
  const issuerId =
    input.requestIssuedBy ?? input.loopCreatedBy ?? input.cycleCreatedBy ?? null;
  return labelForUser(input.users, issuerId, input.displayNamesByUserId) ?? undefined;
}
